import React, { HTMLAttributes, useEffect, useRef, useState } from "react";
import BrandCarousel from "~/Brands/ExpandedFeatureGroupCarousel/BrandCarousel";
import Icon from "~/common/Icons/Standard";

const buttonStyle =
  "bg-white group border border-solid border-gray-600 cursor-pointer absolute rounded-full top-1/2 -translate-y-1/2 p-0 w-8 h-8 group hidden md:flex items-center justify-center hover:bg-gray-100 active:bg-gray-100";

const iconStyle =
  "h-4 w-2 fill-green-500 group-hover:fill-green-700 group-active:fill-green-700";

interface TopperCarouselProps {
  displayType?: number;
  isMultiSelect?: boolean;
  topperItems: React.ReactNode[];
  wrapperProps?: HTMLAttributes<HTMLDivElement>;
}

const Carousel = ({
  displayType,
  isMultiSelect,
  topperItems,
  wrapperProps,
}: TopperCarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [heightAdjustment, setHeightAdjustment] = useState("auto");

  useEffect(() => {
    if (carouselRef.current) {
      const initialHeight = carouselRef.current.offsetHeight;
      setHeightAdjustment(`${initialHeight + 16}px`);
    }
  }, []);

  const isWideStyle = displayType === 1 || displayType === 2;

  const sharedShadowStyle = `absolute hidden pointer-events-none md:inline ${
    isWideStyle ? "w-26" : "w-[118px]"
  }`;

  let rightShadowPositionClass = "md:left-[-133px]";
  if (isMultiSelect) {
    rightShadowPositionClass = "md:left-[-135px]";
  } else if (isWideStyle) {
    rightShadowPositionClass = "md:left-[-115px]";
  }

  const rightShadowStyle = `${sharedShadowStyle} rotate-180 ${rightShadowPositionClass}`;

  const leftShadowStyle = `${sharedShadowStyle} md:left-[47px]`;

  const mobileShadowStyle = `absolute pointer-events-none inline md:hidden w-[60px] ${
    isMultiSelect ? "-bottom-1.5" : ""
  } ${isWideStyle ? "-bottom-2" : ""} ${
    !isWideStyle && !isMultiSelect ? "-bottom-1" : ""
  }`;

  return (
    <div {...wrapperProps} ref={carouselRef}>
      <BrandCarousel
        slideRight={({ onClick }) => (
          <>
            <div
              className={`${mobileShadowStyle} rotate-180 right-0`}
              style={{
                boxShadow:
                  "rgba(0, 0, 0, 0.075) 8px 0px 7px -8px inset, rgba(0,0,0,0.05) 23px 0px 19px -14px inset",
                height: heightAdjustment,
              }}
            />
            <button
              className={`${buttonStyle} ${
                isMultiSelect ? "md:-right-12" : "md:right-[-46px]"
              }`}
              type="button"
              onClick={onClick}
              aria-label="slide carousel right"
              data-testid="topper_right_arrow"
            >
              <div
                className={rightShadowStyle}
                style={{
                  boxShadow:
                    "rgba(0, 0, 0, 0.075) 8px 0px 7px -8px inset, rgba(0,0,0,0.05) 23px 0px 19px -14px inset",
                  height: heightAdjustment,
                }}
              />
              <Icon
                name="navigation-arrow"
                className={iconStyle}
                pointerEvents="none"
              />
            </button>
          </>
        )}
        slideLeft={({ onClick }) => (
          <>
            <button
              className={`${buttonStyle} md:-left-12`}
              type="button"
              onClick={onClick}
              aria-label="slide carousel left"
              data-testid="topper_left_arrow"
            >
              <Icon
                name="navigation-arrow"
                className={`${iconStyle} rotate-180`}
                pointerEvents="none"
              />
              <div
                className={leftShadowStyle}
                style={{
                  boxShadow:
                    "rgba(0, 0, 0, 0.075) 8px 0px 7px -8px inset, rgba(0,0,0,0.05) 23px 0px 19px -14px inset",
                  height: heightAdjustment,
                }}
              />
            </button>
            <div
              className={`${mobileShadowStyle} left-0`}
              style={{
                boxShadow:
                  "rgba(0, 0, 0, 0.075) 8px 0px 7px -8px inset, rgba(0,0,0,0.05) 23px 0px 19px -14px inset",
                height: heightAdjustment,
              }}
            />
          </>
        )}
        listWrapperProps={{
          className: `flex items-start mb-0 ml-0 pb-1 ${
            isWideStyle ? "gap-4" : "gap-2"
          }`,
        }}
      >
        {topperItems}
      </BrandCarousel>
    </div>
  );
};

export default Carousel;
